import {
  Alert,
  AlertColor,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import PrimaryButton from 'components/Button/PrimaryButton';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { routes } from 'utils/constants/routes';
import Currency from 'utils/hooks/getCurrency';
import { pageSubtitleSx } from 'utils/styles/pageHeader';
import SplitPercentTextBox from './SplitPercentTextbox';

interface SplitRevenueProps {
  bookingLineItem: any;
  onSuccess: () => void;
}

const parseMoney = (raw: unknown): number => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = Number(String(raw ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const parsePercent = (value: unknown): number => {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Distribute weights into percentages that sum to exactly 100.00. */
const distributeTo100 = (weights: number[]): number[] => {
  const n = weights.length;
  if (n === 0) return [];
  const sum = weights.reduce((a, b) => a + b, 0);
  const ratios = sum <= 0 ? weights.map(() => 1 / n) : weights.map(w => w / sum);
  const cents = ratios.map(r => Math.floor(r * 10000) / 100);
  const allocated = round2(cents.reduce((a, b) => a + b, 0));
  const leftover = round2(100 - allocated);
  cents[0] = round2(cents[0] + leftover);
  return cents;
};

const formatAllocatedLabel = (sum: number) => {
  const rounded = round2(sum);
  const percent = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return strings.splitRevenue_allocated_label.replace('{percent}', percent);
};

const AllocationMethodTooltip = ({
  title,
  body,
  details,
}: {
  title: string;
  body: string;
  details: string[];
}) => (
  <Box sx={{ p: 0.25, maxWidth: 320 }}>
    <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>{title}</Typography>
    <Typography sx={{ fontSize: 12, lineHeight: 1.45, mb: 0.75 }}>{body}</Typography>
    {details.map(line => (
      <Typography key={line} sx={{ fontSize: 12, lineHeight: 1.45, color: 'rgba(255,255,255,0.85)', mt: 0.35 }}>
        {line}
      </Typography>
    ))}
  </Box>
);

const SplitRevenue = ({ bookingLineItem }: SplitRevenueProps) => {
  const { id } = bookingLineItem.booking;
  const { id: currentLineItemId } = useParams();
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const [pageResponse, setPageResponse] = useState<any[]>([]);

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleCatch = (error: any) => {
    if (error.response) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response.data?.error_description
          ? error.response.data.error_description
          : error.response.data.message,
      });
    } else {
      setMessage({
        display: true,
        severity: 'error',
        message: error.message,
      });
    }
  };

  const getInitialResponse = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.getSplitRevenueById(id));
      setPageResponse(data || []);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void getInitialResponse();
  }, [id]);

  const allActualized = pageResponse.length > 0 && pageResponse.every((row: any) => row.status === 'ACTUALIZED');
  const allocationLocked = !allActualized;

  const totalAmount = useMemo(() => {
    const fromApi = pageResponse[0]?.totalCostExclTaxAmount;
    if (typeof fromApi === 'number' && Number.isFinite(fromApi)) return fromApi;
    return parseMoney(pageResponse[0]?.totalCostExclTax);
  }, [pageResponse]);

  const allocatedSum = useMemo(
    () => round2(pageResponse.reduce((sum, row) => sum + parsePercent(row.splitRevenuePercent), 0)),
    [pageResponse],
  );

  const applyPercents = (percents: number[]) => {
    setPageResponse(prev => prev.map((row, index) => ({ ...row, splitRevenuePercent: percents[index] })));
  };

  const handleSplitEqually = () => {
    applyPercents(distributeTo100(pageResponse.map(() => 1)));
  };

  const handleUseEventRevenue = () => {
    applyPercents(distributeTo100(pageResponse.map((row: any) => Number(row.eventRevenue) || 0)));
  };

  const handleReset = () => {
    setPageResponse(prev => prev.map(row => ({ ...row, splitRevenuePercent: '' })));
  };

  const handleAddEdit = async () => {
    try {
      let errObj = {} as any;
      pageResponse.forEach((p: any) => {
        if (p.splitRevenuePercent === '' || p.splitRevenuePercent == null) {
          errObj = p;
        }
      });
      let msg = '';
      if (Object.keys(errObj).length > 0) {
        msg = `Split Revenue Percent is required for ${errObj?.venue} on ${errObj?.startDate}`;
      } else if (Math.abs(allocatedSum - 100) > 0.009) {
        msg = 'Total should be 100';
      }
      if (msg !== '') {
        setMessage({
          display: true,
          severity: 'error',
          message: msg,
        });
        return;
      }
      const requestBody = pageResponse.map((p: any) => ({
        id: p.id,
        splitRevenuePercent: parseFloat(p.splitRevenuePercent),
      }));
      setLoading(true);
      const { status } = await axiosInstance.put(URLS.getSplitRevenueById(id), requestBody);
      if (status === 200 || status === 201) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.SplitRevenuePropsAddSuccessText,
        });
        getInitialResponse();
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const headerCellSx = {
    fontWeight: 700,
    color: '#666',
    textTransform: 'uppercase' as const,
    fontSize: 11,
    whiteSpace: 'nowrap' as const,
    backgroundColor: '#fafafa',
    py: 0.75,
    px: 2,
  };

  const bodyCellSx = { py: 1.25, px: 2 };

  const progressOver = allocatedSum > 100.009;

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}

      <Typography sx={{ ...pageSubtitleSx, mb: 2 }}>{strings.splitRevenue_subtitle}</Typography>

      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
          {strings.tabs_text_actual_revenue_excl_tax}
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3, mt: 0.25 }}>
          {pageResponse[0]?.totalCostExclTax ?? Currency(totalAmount)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.75, maxWidth: 420 }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, allocatedSum))}
            sx={{
              flex: 1,
              height: 8,
              borderRadius: 999,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                bgcolor: progressOver ? 'error.main' : 'success.main',
              },
            }}
          />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              color: progressOver ? 'error.main' : 'text.secondary',
            }}>
            {formatAllocatedLabel(allocatedSum)}
          </Typography>
        </Box>
      </Box>

      {allocationLocked && pageResponse.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {strings.splitRevenue_incomplete_actualization}
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Tooltip
          arrow
          placement="bottom-start"
          title={
            <AllocationMethodTooltip
              title={strings.splitRevenue_split_equally}
              body={strings.splitRevenue_split_equally_body}
              details={[strings.splitRevenue_split_equally_example_4, strings.splitRevenue_split_equally_example_3]}
            />
          }>
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={allocationLocked || loading}
              onClick={handleSplitEqually}
              endIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {strings.splitRevenue_split_equally}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          arrow
          placement="bottom-start"
          title={
            <AllocationMethodTooltip
              title={strings.splitRevenue_use_event_revenue}
              body={strings.splitRevenue_use_event_revenue_body}
              details={[
                strings.splitRevenue_use_event_revenue_formula,
                strings.splitRevenue_use_event_revenue_example,
              ]}
            />
          }>
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={allocationLocked || loading}
              onClick={handleUseEventRevenue}
              endIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {strings.splitRevenue_use_event_revenue}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="text"
          size="small"
          disabled={allocationLocked || loading}
          onClick={handleReset}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          {strings.splitRevenue_reset}
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>{strings.splitRevenue_column_venue_event}</TableCell>
                <TableCell sx={headerCellSx}>{strings.splitRevenue_column_date}</TableCell>
                <TableCell sx={headerCellSx}>{strings.splitRevenue_column_status}</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {strings.splitRevenue_column_split}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {strings.splitRevenue_column_allocated}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageResponse.map((item: any) => {
                const isCurrent = Number(item.id) === Number(currentLineItemId);
                const isActualized = item.status === 'ACTUALIZED';
                const eventLabel = [item.eventSubtype, item.eventType].filter(Boolean).join(' · ');
                const allocated = totalAmount * (parsePercent(item.splitRevenuePercent) / 100);

                return (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:last-child td': { border: 0 },
                      ...(isCurrent
                        ? {
                            bgcolor: 'rgba(25, 103, 210, 0.06)',
                            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                          }
                        : {}),
                    }}>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{item.venue}</Typography>
                      {eventLabel ? (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.15 }}>{eventLabel}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Link
                        to={`${routes.bookings}/${item.id}`}
                        style={{ color: 'inherit', fontWeight: isCurrent ? 700 : 500 }}>
                        {item.startDate ? dayjs(item.startDate).format('D MMM YYYY') : '—'}
                      </Link>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {isActualized ? (
                        <Chip
                          size="small"
                          label={strings.splitRevenue_status_actualized}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: 11,
                            bgcolor: '#800080',
                            color: '#fff',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          color="warning"
                          label={strings.splitRevenue_status_pending}
                          sx={{ height: 22, fontWeight: 700, fontSize: 11, '& .MuiChip-label': { px: 1 } }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="right">
                      <SplitPercentTextBox item={item} setPageResponse={setPageResponse} isDisabled={allocationLocked} />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }} align="right">
                      {Currency(round2(allocated))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <PrimaryButton
        sx={{ width: screenSizeDownSm ? '100%' : 'max-content', mt: 2 }}
        onClick={handleAddEdit}
        disabled={allocationLocked || loading}>
        {strings.splitRevenue_save}
      </PrimaryButton>
    </>
  );
};

export default SplitRevenue;
